import React, { useEffect, useState } from "react";
import { animate, motion } from "framer-motion";
import { Wallet, Plus, Lock, ArrowUpRight, ArrowDownRight, ChevronRight, Crown } from "lucide-react";
import { EXPENSE_CATS, EXPENSE_BY, PERIODS, fmtEur } from "./constants";
import { hapticTap } from "@/lib/haptics";

export function CountUp({ value, className, testid }) {
  const [v, setV] = useState(value || 0);
  useEffect(() => {
    const c = animate(v, value || 0, { duration: 0.7, ease: [0.22, 1, 0.36, 1], onUpdate: setV });
    return () => c.stop();
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return <span className={className} data-testid={testid}>{fmtEur(v)}</span>;
}

export function PeriodToggle({ period, onChange, idPrefix = "expense-period" }) {
  return (
    <div className="inline-flex p-1 rounded-full bg-[#F4EFE6] border border-[#F0EBE3]">
      {PERIODS.map((p) => (
        <button key={p.id} onClick={(e) => { e.stopPropagation(); hapticTap(); onChange(p.id); }}
          data-testid={`${idPrefix}-${p.id}`}
          className="relative px-3 py-1 rounded-full text-[12px] font-semibold">
          {period === p.id && (
            <motion.span layoutId={`${idPrefix}-pill`} className="absolute inset-0 rounded-full bg-white shadow-sm"
              transition={{ type: "spring", stiffness: 400, damping: 30 }} />
          )}
          <span className={`relative ${period === p.id ? "text-[#1A1A1A]" : "text-[#9A9A9A]"}`}>{p.label}</span>
        </button>
      ))}
    </div>
  );
}

export function CategoryBar({ byCategory, total, height = 10 }) {
  const parts = EXPENSE_CATS.filter((c) => byCategory?.[c.id] > 0);
  return (
    <div className="flex w-full overflow-hidden rounded-full bg-[#F4EFE6]" style={{ height }}>
      {parts.map((c, i) => (
        <motion.div key={c.id} initial={{ width: 0 }} animate={{ width: `${(byCategory[c.id] / (total || 1)) * 100}%` }}
          transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
          style={{ background: c.color }} className="h-full" />
      ))}
    </div>
  );
}

function Delta({ total, prev, period }) {
  if (!prev) return <span className="text-[12px] text-[#9A9A9A]">{PERIODS.find((p) => p.id === period)?.short}</span>;
  const pct = Math.round(((total - prev) / prev) * 100);
  const up = pct > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-0.5 text-[12px] font-semibold" style={{ color: up ? "#E94545" : "#2DBA60" }}>
      <Icon className="h-3.5 w-3.5" /> {Math.abs(pct)}% vs {PERIODS.find((p) => p.id === period)?.prev}
    </span>
  );
}

export function ExpensesTile({ data, dogName, period, onPeriod, onAdd, onOpenList, onLocked }) {
  const locked = !!data?.locked;
  const total = data?.total || 0;
  const top = EXPENSE_CATS.filter((c) => data?.by_category?.[c.id] > 0)
    .sort((a, b) => data.by_category[b.id] - data.by_category[a.id]).slice(0, 3);
  const guard = (fn) => (e) => { e.stopPropagation(); hapticTap(); locked ? onLocked() : fn(); };

  return (
    <motion.div whileHover={{ y: -3 }} onClick={() => (locked ? onLocked() : onOpenList())}
      data-testid="expense-summary-tile"
      className="relative rounded-[28px] p-5 sm:p-6 bg-white border border-[#F0EBE3] overflow-hidden cursor-pointer"
      style={{ boxShadow: "0 18px 40px -24px rgba(180,90,20,0.35)" }}>
      <div aria-hidden className="absolute -top-16 -right-16 h-48 w-48 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,184,60,0.22) 0%, transparent 70%)" }} />
      <div className="relative flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-2xl flex items-center justify-center" style={{ background: "#FFF3E6" }}>
            <Wallet className="h-5 w-5 text-[#FF7A1A]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold">Spese</div>
            <div className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">per {dogName}</div>
          </div>
        </div>
        <PeriodToggle period={period} onChange={onPeriod} />
      </div>

      <div className="relative mt-5">
        <div className={locked ? "blur-[5px] select-none" : ""}>
          <CountUp value={total} testid="expense-total"
            className={`font-display text-5xl sm:text-6xl tracking-tight tabular-nums ${locked ? "text-[#B9B4A8]" : "text-[#1A1A1A]"}`} />
        </div>
        {locked ? (
          <div data-testid="expenses-pro-locked-banner"
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-[#1A1A1A]"
            style={{ background: "linear-gradient(135deg,#FFB020,#F4B83C)" }}>
            <Lock className="h-3.5 w-3.5" /> Sblocca le spese con ORIO Pro
          </div>
        ) : (
          <div className="mt-1"><Delta total={total} prev={data?.prev_total} period={period} /></div>
        )}
      </div>

      <div className={`relative mt-4 ${locked ? "opacity-40 grayscale" : ""}`}>
        <CategoryBar byCategory={data?.by_category} total={total} />
        <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1">
          {top.length ? top.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 text-[12px] text-[#6B6B6B]">
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
              {c.label} · <b className="text-[#1A1A1A] font-semibold">{fmtEur(data.by_category[c.id])}</b>
            </span>
          )) : <span className="text-[12px] text-[#9A9A9A]">Nessuna spesa registrata in questo periodo</span>}
        </div>
      </div>

      <div className="relative mt-4 -mx-1 flex gap-2 overflow-x-auto no-scrollbar px-1 pb-1">
        {EXPENSE_CATS.map((c) => (
          <motion.button key={c.id} whileTap={{ scale: 0.9 }} onClick={guard(() => onAdd(c.id))}
            data-testid={`expense-quick-${c.id}`}
            className="shrink-0 h-[68px] w-[68px] rounded-2xl flex flex-col items-center justify-center gap-1 border"
            style={{ background: locked ? "#F5F2EC" : `${c.color}12`, borderColor: locked ? "#E5DEC9" : `${c.color}33` }}>
            <c.icon className="h-5 w-5" style={{ color: locked ? "#B9B4A8" : c.color }} />
            <span className="text-[10px] font-semibold text-[#6B6B6B] leading-none truncate max-w-[60px]">{c.label.split(" ")[0]}</span>
          </motion.button>
        ))}
      </div>

      <div className="relative mt-4 flex gap-2">
        <motion.button whileTap={{ scale: 0.97 }} onClick={guard(() => onAdd(null))} data-testid="add-expense-trigger-button"
          className="flex-1 py-3 rounded-full text-white text-[14px] font-semibold inline-flex items-center justify-center gap-1.5"
          style={{ background: locked ? "#C9C2B4" : "linear-gradient(135deg,#FF7A1A 0%,#F4B83C 100%)" }}>
          {locked ? <Crown className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {locked ? "Passa a Pro" : "Aggiungi spesa"}
        </motion.button>
        <motion.button whileTap={{ scale: 0.97 }} onClick={guard(onOpenList)} data-testid="expense-open-list"
          className="px-4 py-3 rounded-full border border-[#F0EBE3] bg-white text-[13px] font-semibold text-[#1A1A1A] inline-flex items-center gap-1">
          {data?.count || 0} {data?.count === 1 ? "voce" : "voci"} <ChevronRight className="h-4 w-4" />
        </motion.button>
      </div>
      {!data?.is_pro && !locked && (
        <div className="relative mt-3 text-[11.5px] text-[#B37016] font-medium">Piano Free · 1 spesa di prova inclusa</div>
      )}
    </motion.div>
  );
}

export function categoryOf(id) {
  return EXPENSE_BY[id] || EXPENSE_BY.extra;
}
